import type { Greffer, GreffonInstance } from '@/api/types';
import type { GreffonStatus, GrefferStatus } from '@/components/ui';

// Backend numeric status codes (manager/backend/apps/constants/status.py).
export const GREFFER = { CREATED: 0, REGISTERING: 1, REGISTERED: 2 } as const;
export const GREFFON = { CREATED: 0, STARTING: 1, STARTED: 2, STOPPING: 3, STOPPED: 4 } as const;

/**
 * "This machine is usable" — the single definition, because two of them drifted.
 *
 * REGISTERED alone is not reachable: a tunnel accept whose readiness wait times
 * out commits the row as REGISTERED with readiness still unproven, and
 * instances scheduled there answer 502.
 *
 * Onboarding and the connect flow BOTH need this, in opposite directions
 * (`some(connected)` to unlock the next step, `filter(!connected)` to find the
 * machine still being set up). While each spelled it out separately, tightening
 * one left the other resolving nothing, so the connect flow's only remaining
 * action was to create a SECOND, billable greffer. One predicate makes that
 * particular drift unrepresentable.
 *
 * Absent field = a manager predating readiness: connected. Only an explicit
 * timestamp withholds it, and only on a tunnel row.
 */
export function isGrefferConnected(
  greffer: Pick<Greffer, 'status' | 'mode' | 'tunnel_readiness_unproven_since'>,
): boolean {
  if (greffer.status !== GREFFER.REGISTERED) return false;
  return !(greffer.mode === 'tunnel' && greffer.tunnel_readiness_unproven_since);
}

export function grefferStatusKey(status: number): GrefferStatus {
  if (status === GREFFER.REGISTERED) return 'registered';
  if (status === GREFFER.REGISTERING) return 'registering';
  if (status === GREFFER.CREATED) return 'created';
  // Anything else is a state this client predates. Reporting it as 'created'
  // -- which this did for every value outside 0..2 -- is the same
  // infer-what-you-cannot-read bug the connect flow had one layer up, and it
  // is what made "go to the machines screen" a dead end: the user left an
  // honest "unrecognised" panel and was shown a confident, wrong "Created".
  return 'unknown';
}

/**
 * Greffer chip key composing registration status with heartbeat liveness
 * (greffer-observability epic). A REGISTERED greffer that has gone silent shows
 * 'unreachable'; one reporting failed collection shows 'degraded'. 'unknown'
 * liveness (never heard a heartbeat — a legacy/just-accepted greffer) keeps the
 * green 'registered' so it is not falsely alarmed. No liveness field → today's
 * registration-only behaviour.
 */
export function grefferChipKey(
  greffer: Pick<Greffer, 'status' | 'liveness' | 'mode' | 'tunnel_readiness_unproven_since'>,
): GrefferStatus {
  if (greffer.status === GREFFER.REGISTERED) {
    // Widen to string: liveness is JSON parsed at runtime, so a manager newer
    // than this build can send a value outside the declared union. Comparing as
    // a string lets the defensive fallback below actually be reachable.
    const lv = greffer.liveness as string | undefined;
    if (lv === 'unreachable') return 'unreachable';
    // Readiness outranks 'degraded' and is outranked by 'unreachable'.
    //
    // Above degraded because the two differ in what the USER feels: degraded
    // is failed metric collection on a node that is otherwise serving, while
    // an unproven tunnel means instances scheduled here answer 502. Below
    // unreachable because no heartbeat at all is the truer, larger statement
    // -- an unproven tunnel on a node we cannot hear from is not the headline.
    //
    // Guarded on mode as well as the field. The backend only sets it for
    // tunnel rows, but a proxy greffer painted 'Connecting' would be a state
    // it can never leave, and trusting one field to imply another across a
    // version skew is how the liveness fallback below got written.
    if (greffer.mode === 'tunnel' && greffer.tunnel_readiness_unproven_since) return 'connecting';
    if (lv === 'degraded') return 'degraded';
    // 'unknown' = registered but never heard from (legacy/just-accepted). Per
    // the AC the greffer card must flag it (not shown green). Its instances,
    // though, stay on their real status (see greffonChipKey): un-heard-from is
    // not confirmed stale, so the card flags while the instances stay actionable.
    if (lv === 'unknown') return 'liveness-unknown';
    // Only a missing field or an explicit 'live' is a green Registered. Any
    // other value is a liveness string this client predates (version skew):
    // treat it as neutral 'liveness-unknown' rather than falsely painting it
    // green, mirroring StatusChip's defensive UNKNOWN fallback.
    if (lv != null && lv !== 'live') return 'liveness-unknown';
    return 'registered';
  }
  return grefferStatusKey(greffer.status);
}

// The exact set of values the backend's GreffonInstance.display_state() emits
// (GREFFON_STATUS_DISPLAY + the two derived states). Note 'greffer-not-running'
// is a client-only key (from greffonStatusKey), never a server display_state,
// so it is intentionally NOT here.
const DISPLAY_STATE_KEYS = new Set<GreffonStatus>([
  'started',
  'stopped',
  'starting',
  'stopping',
  'created',
  'missing-on-greffer',
  'state-unknown',
  // Backup/restore op-window overlay (Feature #8). Without these the chip falls
  // through to the raw STOPPED int during a cold backup/restore (the instance is
  // stopped for the window) and leaks a false outage.
  'backing-up',
  'restoring',
]);

/**
 * Instance chip key: prefer the server-derived `display_state` (which overlays
 * greffer liveness and surfaces missing-on-greffer / state-unknown). Fall back
 * to the client-derived key when the field is absent or unrecognized, so the UI
 * keeps working against an older backend.
 */
export function greffonChipKey(instance: GreffonInstance): GreffonStatus {
  // A cross-greffer migration in flight overlays everything else. The instance is
  // stopped on the source for the backstop and the move is invisible to
  // display_state (the backstop bypasses the op-window), so without this the chip
  // would read a false 'Stopped' / 'Status unknown'. Only a RUNNING move takes the
  // chip; a recently-FAILED one leaves the real status (its failure surfaces in
  // the migration panel/dialog, not the chip).
  if (instance.active_migration?.status === 'running') return 'migrating';
  // A greffer that isn't REGISTERED (e.g. mid re-registration) drives the action
  // buttons to 'greffer-not-running' via greffonStatusKey; keep the chip on the
  // same key so the chip and the buttons can't contradict each other. The
  // server display_state derives from liveness only and is ignored here.
  if (instance.greffer && instance.greffer.status !== GREFFER.REGISTERED) {
    return 'greffer-not-running';
  }
  // The backend collapses BOTH 'unreachable' (heard then silent) and 'unknown'
  // (never heard from — legacy/just-accepted) into display_state 'state-unknown'.
  // Only 'unreachable' means the status is actually stale. For an 'unknown'
  // greffer the status is NOT stale (the legacy per-transition callback still
  // updates it, and a just-accepted greffer simply hasn't beaten yet), and the
  // greffer card already flags it as 'Liveness unknown'. So show the real status
  // here, leaving the instance actionable — anything else would wrongly mark the
  // whole mixed-fleet / freshly-accepted population 'unreachable'.
  if (instance.greffer?.liveness === 'unknown') return greffonStatusKey(instance);
  // An unreachable greffer means the instance's real state is stale, full stop.
  // Drive 'state-unknown' from liveness directly, not only via the server
  // display_state: if the backend reported liveness but omitted display_state,
  // the chip (and the start/stop gating that keys off it) must still neutralize,
  // or the instance card would contradict the greffer card showing 'Unreachable'.
  if (instance.greffer?.liveness === 'unreachable') return 'state-unknown';
  const ds = instance.display_state;
  if (ds && DISPLAY_STATE_KEYS.has(ds as GreffonStatus)) return ds as GreffonStatus;
  return greffonStatusKey(instance);
}

/** Compact relative "last seen" label for a greffer liveness tooltip. */
export function formatLastSeen(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** An instance on a greffer that isn't registered/up can't run, regardless of its own status. */
export function greffonStatusKey(instance: GreffonInstance): GreffonStatus {
  if (instance.greffer && instance.greffer.status !== GREFFER.REGISTERED) return 'greffer-not-running';
  switch (instance.status) {
    case GREFFON.STARTED:
      return 'started';
    case GREFFON.STARTING:
      return 'starting';
    case GREFFON.STOPPING:
      return 'stopping';
    case GREFFON.CREATED:
      return 'created';
    default:
      return 'stopped';
  }
}

export const isTransient = (status: number) => status === GREFFON.STARTING || status === GREFFON.STOPPING;

/** Config is applied at start, so only a stopped/created greffon is configurable. */
export const isConfigurable = (status: number) => status === GREFFON.STOPPED || status === GREFFON.CREATED;

/**
 * The instance is mid backup/restore (the server op-window display overlay,
 * Feature #8). The stored `status` int is unchanged during the window, so the
 * action buttons (Start/Stop/Configure) must gate off THIS, not the raw int, or
 * they would offer a conflicting Stop/Start over a cold backup/restore.
 */
export function isOpInProgress(instance: Pick<GreffonInstance, 'display_state'>): boolean {
  return instance.display_state === 'backing-up' || instance.display_state === 'restoring';
}

/**
 * The instance is held by ANY exclusive operation: a cold backup/restore
 * (op-window display overlay) OR a running cross-greffer move. A move is
 * invisible to display_state, so the two must be OR-ed. Detail-page panels
 * (backup, restore, move) gate their trigger actions off this shared signal so a
 * user can't fire a conflicting request (which the backend rejects as
 * instance_busy) while a backup/restore/move is already running.
 */
export function isInstanceBusy(
  instance: Pick<GreffonInstance, 'display_state' | 'active_migration'>,
): boolean {
  return isOpInProgress(instance) || instance.active_migration?.status === 'running';
}
